import type { Article } from "./types";

/*
 * Article registry — every story routes to /{section}/{article-slug}
 * through the reusable ArticlePage template. Placeholder imagery is
 * distinct per story and features African American editorial subjects.
 */

export const articles: Article[] = [
  // ── THE TEA ──────────────────────────────────────────────────────────
  {
    slug: "the-art-of-knowing-when-to-speak",
    section: "the-tea",
    title: "The Art of Knowing When to Speak — and When to Sip",
    dek: "Everybody has an opinion. Not everybody has the range. On timing, taste, and the power of a well-held pause.",
    image: "/manus-storage/article-tea-1_8cdf3d61.png",
    imageAlt: "African American woman sipping a gold-rimmed cocktail with a knowing smirk",
    author: "Porsha",
    date: "2026-08-24",
    readTime: "6 min read",
    tags: ["Commentary", "Culture"],
    featured: true,
    body: [
      "There is a particular kind of power in the pause. Watch any room long enough and you will notice: the person who speaks last usually says the thing that lands. The rest of the room is just warming up the air.",
      "I learned this early, sitting at my grandmother's kitchen table while the grown folks talked. The women I admired most never rushed to fill silence. They let the moment breathe, took a slow sip of whatever was in their glass, and then — only then — delivered the sentence everyone would repeat on the drive home.",
      "We live in an era that rewards the fastest take, not the truest one. Timelines move at the speed of impulse, and somewhere between the group chat and the quote-tweet, discernment got mistaken for hesitation. Let me be clear: they are not the same thing. Hesitation is fear wearing a watch. Discernment is taste wearing time.",
      "So here is my rule, and you can borrow it: if the room is loud, be quiet a little longer. If the take is easy, hold it up to the light first. And if someone tells you that you are 'too much' for waiting until you actually know — pour another glass and let them talk.",
      "Because the tea is always better when it has had time to steep.",
    ],
    pullQuote: "Hesitation is fear wearing a watch. Discernment is taste wearing time.",
  },
  {
    slug: "group-chat-diplomacy",
    section: "the-tea",
    title: "Group Chat Diplomacy: A Field Guide",
    dek: "Every group chat has a diplomat, a instigator, and somebody's cousin who only sends memes. Which one are you?",
    image: "/manus-storage/article-tea-3-v2_913858ad.png",
    imageAlt: "Two African American women whispering and laughing at a gallery opening",
    author: "Porsha",
    date: "2026-08-18",
    readTime: "5 min read",
    tags: ["Culture", "Humor"],
    body: [
      "The group chat is the modern salon. It is where news breaks, where outfits get approved, and where friendships go to be tested by the simple question: 'y'all saw this?'",
      "After years of participant observation, I can confirm every thriving chat has three essential roles. The Diplomat smooths things over when a joke lands sideways. The Instigator drops the link with no context and vanishes. And the Archivist remembers exactly what you said in March of 2023, with receipts.",
      "The unwritten rules are simple. Never leave someone on read during a crisis. Voice notes over forty seconds require a warning. And if somebody types 'we need to talk' without a follow-up within five minutes, that is a federal offense.",
      "Treasure your chat. Water it with memes. Defend it from screenshots. It is, after all, the last honest room most of us have.",
    ],
  },
  {
    slug: "why-every-icon-needs-a-podcast-moment",
    section: "the-tea",
    title: "Why Every Icon Eventually Gets a Microphone",
    dek: "The podcast era didn't create the culture's loudest voices — it just finally handed them the aux cord.",
    image: "/manus-storage/article-tea-2-v2_ff9904b1.png",
    imageAlt: "African American man speaking into a podcast microphone in a dim studio",
    author: "Porsha",
    date: "2026-08-10",
    readTime: "7 min read",
    tags: ["Media", "Commentary"],
    editorsPick: true,
    body: [
      "There was a time when the culture's best conversations happened in kitchens, barbershops, and the last hour of the cookout. The microphone didn't invent those voices. It just finally caught up to them.",
      "What makes the medium irresistible is intimacy. A podcast is not a performance; it is a porch. The best hosts understand that the audience isn't listening for polish — they're listening for recognition. The 'girl, same' moment is worth more than any production budget.",
      "But a microphone is a responsibility. The same intimacy that builds trust can launder nonsense at scale. The icons who last are the ones who treat the mic like a dinner table: everybody eats, nobody performs, and the check comes due for whatever you said.",
      "So yes, everybody has a podcast now. The question was never who has one. It's who still has something to say when the episode ends.",
    ],
    pullQuote: "A podcast is not a performance; it is a porch.",
  },

  // ── FASHION ──────────────────────────────────────────────────────────
  {
    slug: "gold-is-a-neutral",
    section: "fashion",
    title: "Gold Is a Neutral: Dressing Like the Occasion Deserves",
    dek: "Somewhere along the way we agreed to save the good jewelry for later. Later is a scam.",
    image: "/manus-storage/article-fashion-1-v2_30c00a96.png",
    imageAlt: "African American woman in a structured gold lamé blazer under a spotlight",
    author: "Porsha",
    date: "2026-08-22",
    readTime: "5 min read",
    tags: ["Style", "Lookbook"],
    featured: true,
    body: [
      "There is a drawer in almost every woman's home where the good things go to wait. The earrings that are 'too much for Tuesday.' The dress with the tag still on, preserved for an occasion that has not yet been invented.",
      "I am here to stage an intervention. Gold is a neutral. Sequins before noon are a personality trait, not a crime. And the occasion — darling — is that you are alive and the mirror is available.",
      "The women whose style we canonize never waited for permission. They understood that dressing well is not about the event; it is about the standard. When you treat an ordinary day like it deserves your best, your best stops feeling like a costume and starts feeling like a signature.",
      "Start small if you must: the good perfume on a grocery run, the silk scarf on a Zoom call. But start. Later is a scam, and your closet knows it.",
    ],
    pullQuote: "The occasion is that you are alive and the mirror is available.",
  },
  {
    slug: "the-finishing-touch-report",
    section: "fashion",
    title: "The Finishing Touch Report: Rings, Scarves, and Intent",
    dek: "Accessories are not decoration. They are punctuation — and this season, the sentence ends in gold.",
    image: "/manus-storage/article-fashion-2_8939c8aa.png",
    imageAlt: "Gold stacked rings and a blush silk scarf on a black designer handbag",
    author: "Porsha",
    date: "2026-08-15",
    readTime: "4 min read",
    tags: ["Accessories", "Trends"],
    body: [
      "A great outfit without accessories is a sentence without punctuation — technically complete, emotionally flat. This season's finishing touches are unapologetic: stacked gold rings that click when you gesture, silk scarves tied with intent, hardware that catches light across a room.",
      "The rule I live by is one of emphasis, not accumulation. Choose the place you want the eye to land — the hand, the neck, the bag — and let everything else be the supporting cast.",
      "And a note on the scarf: learn three ties. The neckerchief, the bag wrap, and the headscarf your grandmother would approve of. Between those three, there is no outfit that cannot be rescued.",
    ],
  },
  {
    slug: "menswear-moment-black-suit",
    section: "fashion",
    title: "The Black Suit Is Having a Moment — Again",
    dek: "Tailoring never left, but the attitude came back. On cufflinks, pocket squares, and dressing like the evening matters.",
    image: "/manus-storage/article-fashion-3-v2_bb12d883.png",
    imageAlt: "African American man in an all-black tailored suit adjusting his cufflinks",
    author: "Porsha",
    date: "2026-08-06",
    readTime: "5 min read",
    tags: ["Menswear", "Style"],
    body: [
      "Every few years, fashion rediscovers the black suit the way we rediscover a classic album — loudly, and with a claim that this time we really understand it.",
      "The current moment is about restraint with one flourish. The suit is quiet: clean shoulder, exact break at the shoe. The flourish is personal — a gold pocket square, a vintage watch, cufflinks with a story. One signal, not five.",
      "Fit remains the whole sermon. An inexpensive suit tailored to your body will always outdress an expensive one that isn't. Find a tailor, learn their name, tip them well. It is the most stylish money you will ever spend.",
    ],
  },

  // ── TRAVEL ───────────────────────────────────────────────────────────
  {
    slug: "golden-hour-itineraries",
    section: "travel",
    title: "Golden-Hour Itineraries: How I Plan a Trip Around the Light",
    dek: "Forget the checklist. The best trips are built around one hour of perfect light and everything else falls into place.",
    image: "/manus-storage/article-travel-1_36f736e0.png",
    imageAlt: "African American couple walking along an infinity pool at golden hour",
    author: "Porsha",
    date: "2026-08-20",
    readTime: "8 min read",
    tags: ["Destinations", "Luxury"],
    featured: true,
    body: [
      "Every traveler I admire plans differently, but the great ones share a secret: they build the day around the light. Golden hour is not a photo opportunity; it is an appointment. Everything else — the museum, the market, the long lunch — is scheduled in service of being exactly where you want to be when the sky does its best work.",
      "My method is simple. One anchor experience per day, never two. Mornings are for wandering, because cities confess their secrets before ten. Afternoons are for long meals and longer naps. And the hour before sunset belongs to whatever view made you book the flight.",
      "Pack half of what you think you need and leave room for what the trip will give you. The linen you buy there will always mean more than the linen you packed.",
      "Travel is the one purchase that pays you back in a currency that never deflates. Spend accordingly.",
    ],
    pullQuote: "Golden hour is not a photo opportunity; it is an appointment.",
  },
  {
    slug: "the-market-at-dusk",
    section: "travel",
    title: "The Market at Dusk: A Love Letter to Wandering",
    dek: "The best thing on any itinerary is the thing that wasn't on it.",
    image: "/manus-storage/article-travel-market-dusk_ed41a3c9.png",
    imageAlt: "African American woman exploring an old-city street market at dusk",
    author: "Porsha",
    date: "2026-08-12",
    readTime: "6 min read",
    tags: ["Wanderlust", "City Guides"],
    body: [
      "There is a specific hour in every old city when the market turns from commerce to theater. The lanterns come on, the vendors relax into themselves, and the tourists with itineraries have all gone back to their hotels to change for dinner.",
      "That is the hour to be there. Buy the thing you cannot name. Taste the thing you cannot pronounce. Let someone tell you a story about their grandmother's recipe even if you only understand every third word — you will understand the important ones.",
      "Wandering is a skill, and like all skills it requires practice and a little courage. Put the phone away for one hour. The map will still be there when you get back, but the moment won't.",
    ],
  },
  {
    slug: "the-five-star-staycation",
    section: "travel",
    title: "The Five-Star Staycation, Done Properly",
    dek: "Room service, a robe, and nowhere to be. The case for checking into your own city like a guest of honor.",
    image: "/manus-storage/article-travel-staycation_96063512.png",
    imageAlt: "African American woman enjoying breakfast on a five-star hotel terrace at sunrise",
    author: "Porsha",
    date: "2026-08-02",
    readTime: "5 min read",
    tags: ["Staycation", "Luxury"],
    body: [
      "Here is a radical act of self-regard: book the nice hotel in your own city. Not because you need a bed, but because you need the ceremony — the lobby scent, the robe, the breakfast that arrives on a cart like it has somewhere to be.",
      "The staycation done properly has rules. Check in early, check out late. Order the thing you would never make at home. Take the long bath. Watch the skyline do its evening performance from a window you didn't have to clean.",
      "We spend so much energy planning escapes that we forget luxury is sometimes just a change of venue, not a change of continent. Your own city, seen from the twelfth floor, is a destination too.",
    ],
  },

  // ── HAUTE TO WATCH ───────────────────────────────────────────────────
  {
    slug: "the-founder-who-pitches-like-a-poet",
    section: "haute-to-watch",
    title: "The Founder Who Pitches Like a Poet",
    dek: "She walked into the boardroom with samples, a story, and a number nobody could argue with. Meet the first name on this season's watch list.",
    image: "/manus-storage/article-haute-1-v2_c0da5164.png",
    imageAlt: "Young African American woman founder pitching in an elegant dark boardroom",
    author: "Porsha",
    date: "2026-08-21",
    readTime: "9 min read",
    tags: ["Profiles", "Entrepreneurs"],
    featured: true,
    body: [
      "The best pitches don't sound like pitches. They sound like the first chapter of a book you didn't know you were waiting for — and by the time she sets the third sample on the table, the room has already said yes.",
      "What separates the founders on this season's watch list is not hustle; everybody hustles. It is taste. They know which details matter, which meetings to decline, and which version of the product the world is actually ready for.",
      "I asked her what she would tell her younger self, the one building the first prototype at a kitchen table. She didn't hesitate: 'Charge more. Apologize less. The right room will find you, but only if you stop auditioning for the wrong ones.'",
      "Remember the name. You will be hearing it again — first from me, then from everyone.",
    ],
    pullQuote: "Charge more. Apologize less.",
  },
  {
    slug: "the-creator-economy-grows-up",
    section: "haute-to-watch",
    title: "The Creator Economy Grows Up",
    dek: "The ring light era is over. The studio era has begun — and the creators who treat it like a business are the ones to watch.",
    image: "/manus-storage/article-haute-creator_9513d3eb.png",
    imageAlt: "African American entrepreneur working in a sophisticated creative studio",
    author: "Porsha",
    date: "2026-08-14",
    readTime: "7 min read",
    tags: ["Creators", "Business"],
    editorsPick: true,
    body: [
      "There was a season when a ring light and a opinion were a business plan. That season has ended. The creators ascending now look less like influencers and more like studios — with editors, strategies, and a point of view sharp enough to build a brand on.",
      "The shift is maturity, not scale. The ones to watch are not chasing every platform; they are building one room, furnishing it well, and letting the audience come to them.",
      "Ownership is the quiet theme of this era. Own the list, own the feed, own the relationship with the people who show up for you. Algorithms are landlords. The smart ones are buying the building.",
    ],
  },

  // ── DETROIT GUIDE ────────────────────────────────────────────────────
  {
    slug: "detroit-after-dark",
    section: "detroit",
    title: "Detroit After Dark: A Love Letter in Three Courses",
    dek: "The kitchen pass lights glow like a stage. The city eats late, eats well, and tells you the truth. A night out, the Detroit way.",
    image: "/manus-storage/article-detroit-1_7f52a296.png",
    imageAlt: "African American chef plating a dish in a moody upscale Detroit kitchen",
    author: "Porsha",
    date: "2026-08-19",
    readTime: "8 min read",
    tags: ["Dining", "City Guide"],
    featured: true,
    body: [
      "Detroit doesn't perform for visitors. That's the first thing to love about it. The city eats the way it does everything else — with intention, a little swagger, and no interest in your expectations.",
      "Start where the kitchen is the show. The best rooms in this city treat the pass like a stage, and if you sit close enough, dinner comes with choreography. Order what the chef is proud of, not what the internet told you about.",
      "Second course is a record shop that stays open too late, because this is Detroit and the music is not a playlist — it is a bloodline. Let the person behind the counter hand you something. Say yes.",
      "End the night somewhere with a jukebox and a slice of pie. Detroit after dark is not loud about its magic. It just assumes you were raised well enough to notice.",
    ],
    pullQuote: "Detroit after dark is not loud about its magic. It assumes you were raised well enough to notice.",
  },
  {
    slug: "the-record-shop-test",
    section: "detroit",
    title: "The Record Shop Test: How I Judge a City's Soul",
    dek: "Every city has a skyline. Only the real ones have a record shop where the clerk argues with you about side B.",
    image: "/manus-storage/article-detroit-record-shop_88d4b952.png",
    imageAlt: "African American man browsing vinyl in a warmly lit Detroit record shop",
    author: "Porsha",
    date: "2026-08-08",
    readTime: "6 min read",
    tags: ["Music", "Culture"],
    body: [
      "I have a test for every city, and it has never failed me. Skip the monument. Find the record shop. If the person behind the counter has an opinion about what you're holding — a real one, with heat in it — the city has a soul.",
      "Detroit passes this test with extra credit. This is a city where music is genealogy, where the clerks are archivists and the arguments about side B are a form of love.",
      "Buy the record they hand you, even if you don't know it. Especially if you don't know it. A city's taste is a gift; the polite thing is to accept it.",
    ],
  },

  // ── THINGS TO DO ─────────────────────────────────────────────────────
  {
    slug: "the-jazz-club-door-policy",
    section: "things-to-do",
    title: "The Jazz Club Door Policy: Where to Be This Weekend",
    dek: "Candlelight, a brass section, and a room full of people who dressed on purpose. Your weekend, curated.",
    image: "/manus-storage/article-events-1_eb0bd047.png",
    imageAlt: "African American woman in a sequined dress dancing at a candlelit jazz club",
    author: "Porsha",
    date: "2026-08-23",
    readTime: "5 min read",
    tags: ["Nightlife", "Weekend"],
    featured: true,
    body: [
      "There are two kinds of weekends: the ones that happen to you and the ones you dress for. This is a guide to the second kind.",
      "Start with the room that has a door policy — not the velvet-rope kind, the candlelight kind. The jazz club where the band starts late on purpose and the saxophone player closes his eyes like the rest of us are incidental. We are. That's the point.",
      "Then the gallery opening, because the best people-watching in any city happens next to a painting nobody understands yet. Hold a glass. Nod thoughtfully. You belong there.",
      "End where the DJ knows the difference between a crowd and a congregation. Dance like the weekend was your idea. It was.",
    ],
  },
  {
    slug: "gallery-nights-and-champagne",
    section: "things-to-do",
    title: "Gallery Nights & Champagne: The Social Season Opens",
    dek: "The exhibits worth your heels, the openings worth your calendar, and how to work a room like you curated it.",
    image: "/manus-storage/article-events-2_ce5a67e3.png",
    imageAlt: "Fashionable African American guests mingling at an art gallery opening",
    author: "Porsha",
    date: "2026-08-16",
    readTime: "6 min read",
    tags: ["Art", "Events"],
    body: [
      "The social season doesn't announce itself; it simply assumes you're ready. Gallery nights are its opening act — equal parts art, atmosphere, and the quiet competition of who wore it best.",
      "A word on working the room: lead with curiosity, not credentials. Ask someone what piece they keep returning to. The answers are better than the small talk, and occasionally better than the art.",
      "And yes, take the champagne. You're not just attending the culture. You are the culture.",
    ],
  },
  {
    slug: "the-dj-knows",
    section: "things-to-do",
    title: "The DJ Knows: A Field Guide to the Perfect Night Out",
    dek: "Every great night has a moment when the room becomes a single organism. Here's how to find the rooms where it happens.",
    image: "/manus-storage/article-events-dj_e744ea9e.png",
    imageAlt: "African American woman DJ performing at an upscale violet-lit lounge",
    author: "Porsha",
    date: "2026-08-05",
    readTime: "5 min read",
    tags: ["Nightlife", "Music"],
    editorsPick: true,
    body: [
      "You can tell within four songs whether a DJ is playing at a room or reading it. The great ones treat the night like a conversation — they say something, watch how the room answers, and respond.",
      "The perfect night out is architecture, not accident. Arrive after the room has warmed but before it has peaked. Tip the bartender early. And when the DJ drops the record that makes the whole room exhale at once — be on the floor, not in line for the bathroom.",
      "These are the nights that become stories. Dress accordingly.",
    ],
  },

  // ── CURRENT EVENTS ───────────────────────────────────────────────────
  {
    slug: "the-story-behind-the-headline",
    section: "current-events",
    title: "The Story Behind the Headline: What the Chyron Won't Tell You",
    dek: "The news cycle moves fast. The truth moves at its own pace. On reading slower than the scroll.",
    image: "/manus-storage/article-news-1-v2_50f74fb8.png",
    imageAlt: "African American news anchor at a sleek desk with a dusk city skyline behind her",
    author: "Porsha",
    date: "2026-08-25",
    readTime: "7 min read",
    tags: ["Media", "Analysis"],
    featured: true,
    body: [
      "The headline is the trailer, not the film. Somewhere between the push alert and the panel segment, the actual story — the one with context, history, and human beings in it — gets trimmed for time.",
      "My practice is simple: read one story a day all the way to the last paragraph. Not the headline, not the comments, the whole piece. The last paragraph is where the nuance lives, because it's the part the fewest people reach.",
      "We are the most informed and least oriented generation in history — drowning in updates, starving for understanding. The fix isn't more news. It's more attention.",
      "The chyron will keep moving whether you understand the story or not. Understanding is a choice. Choose it.",
    ],
    pullQuote: "The last paragraph is where the nuance lives.",
  },
  {
    slug: "morning-pages-deadlines",
    section: "current-events",
    title: "Morning Pages & Deadlines: Notes from the Desk",
    dek: "Coffee, a microphone, and the discipline of caring about the right things. How the coverage gets made.",
    image: "/manus-storage/article-news-2_17095faa.png",
    imageAlt: "Hands typing on a laptop beside a microphone and morning coffee",
    author: "Porsha",
    date: "2026-08-11",
    readTime: "5 min read",
    tags: ["Behind the Scenes", "Media"],
    body: [
      "People imagine covering the news is adrenaline. Mostly it's coffee, notes, and the discipline of deciding what actually deserves your attention before the day decides for you.",
      "My desk has three rules. Verify before you amplify. Context before commentary. And never let the urgency of the timeline borrow the authority of your voice.",
      "The work is to care about the right things in the right order. Everything else is typing.",
    ],
  },

  // ── DATE NIGHT ───────────────────────────────────────────────────────
  {
    slug: "the-candlelit-rooftop",
    section: "date-night",
    title: "The Candlelit Rooftop: A Date Night That Actually Delivers",
    dek: "City lights, a slow dance, and a reservation worth dressing for. The evening, engineered for romance.",
    image: "/manus-storage/article-date-1-v2_428a4e09.png",
    imageAlt: "African American couple slow dancing on a candlelit rooftop terrace at night",
    author: "Porsha",
    date: "2026-08-26",
    readTime: "6 min read",
    tags: ["City Date Ideas", "Luxury Experiences"],
    featured: true,
    body: [
      "A great date night is not an expense; it is an edit. One beautiful room beats three mediocre stops. The rooftop at dusk, when the city is deciding between gold and blue, is worth a whole itinerary of lesser plans.",
      "The formula: arrive before the reservation and take the long way up. Order the bottle, not the glasses. Put the phones in the bag — both of them, no exceptions — and let the skyline do the flexing.",
      "And when the music finds you, dance. Even badly. Especially badly. Romance is not a performance for the room; it's a conspiracy of two.",
      "This is the evening they'll ask you about later. Plan it like you mean it.",
    ],
    pullQuote: "Romance is not a performance for the room; it's a conspiracy of two.",
  },
  {
    slug: "a-table-for-two-done-right",
    section: "date-night",
    title: "A Table for Two, Done Right",
    dek: "Gold flatware, blush roses, and the lost art of the unhurried dinner. What the best romantic restaurants understand.",
    image: "/manus-storage/article-date-2_f9a1baee.png",
    imageAlt: "Candlelit dinner table for two with gold flatware and blush roses",
    author: "Porsha",
    date: "2026-08-17",
    readTime: "5 min read",
    tags: ["Romantic Restaurants", "His & Hers"],
    editorsPick: true,
    body: [
      "The best romantic restaurants share a secret: they are not selling food. They are selling time — the unhurried kind, where the second course arrives when the conversation is ready for it.",
      "Look for the rooms that understand lighting is a love language. Candlelight flatters everyone; overhead fluorescents flatter no one. If the table has roses and the flatware has weight, the kitchen usually has standards.",
      "His & hers rule for the evening: one of you orders the appetizer, the other orders the dessert, and both of you share everything. A table for two is a small country. Govern it generously.",
    ],
  },
];

export const getArticlesBySection = (sectionSlug: string) =>
  articles.filter((a) => a.section === sectionSlug);

export const getArticle = (sectionSlug: string, articleSlug: string) =>
  articles.find((a) => a.section === sectionSlug && a.slug === articleSlug);

export const featuredArticles = articles.filter((a) => a.featured);
export const editorsPicks = articles.filter((a) => a.editorsPick);
